/**
 * Display-only currency conversion. Does not change Shopify cart/checkout currency.
 */
(function () {
  const config = window.themeVariables?.displayCurrency;
  if (!config?.enabled) return;

  const ZERO_DECIMAL = new Set(['KRW', 'THB']);
  const STORAGE_KEY = 'theme:display-currency';

  // Mirror of snippets/currency-symbol.liquid — used to re-label the toggle on change.
  const SYMBOLS = {
    USD: '$', THB: '฿', KRW: '₩', GBP: '£', EUR: '€',
    SGD: 'S$', AUD: 'A$', CAD: 'C$', VND: '₫',
  };

  function getRate(code) {
    const entry = config.currencies.find((item) => item.code === code);
    return entry?.rate ?? 1;
  }

  function formatCents(cents, code) {
    const rate = getRate(code);
    const amount = (Number(cents) / 100) * rate;
    const fractionDigits = ZERO_DECIMAL.has(code) ? 0 : 2;

    let formatted = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amount);

    if (code === config.baseCurrency && config.stripUsdDecimals) {
      formatted = formatted.replace(/\.00$/, '');
    }

    return formatted;
  }

  function rememberOriginal(element) {
    if (!element.dataset.moneyOriginal) {
      element.dataset.moneyOriginal = getVisiblePrice(element);
    }
  }

  function getVisiblePrice(element) {
    let visible = '';
    element.childNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains('sr-only')) return;
      visible += node.textContent;
    });
    return visible.trim();
  }

  function setVisiblePrice(element, text) {
    const srOnly = element.querySelector('.sr-only');
    [...element.childNodes].forEach((node) => {
      if (node === srOnly) return;
      element.removeChild(node);
    });
    element.append(document.createTextNode(text));
  }

  function updateElement(element, centsOverride) {
    const cents = centsOverride ?? element.dataset.moneyCents;
    if (cents === undefined || cents === '') return;

    rememberOriginal(element);

    const selected = DisplayCurrency.getSelected();
    if (selected === config.baseCurrency) {
      setVisiblePrice(element, element.dataset.moneyOriginal);
      return;
    }

    const formatted = formatCents(cents, selected);
    const template = element.dataset.priceTemplate;

    setVisiblePrice(
      element,
      template ? template.replace('[[price]]', formatted) : formatted
    );
  }

  function apply(root = document) {
    root.querySelectorAll('[data-money-cents]').forEach((element) => {
      updateElement(element);
    });
  }

  const DisplayCurrency = {
    getSelected() {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && config.currencies.some((item) => item.code === stored)) {
        return stored;
      }
      return config.baseCurrency;
    },

    setSelected(code) {
      if (!config.currencies.some((item) => item.code === code)) return;
      localStorage.setItem(STORAGE_KEY, code);
      apply();
      document.dispatchEvent(
        new CustomEvent('display-currency:change', { detail: { code } })
      );
    },

    formatCents,
    updateElement,
    apply,
    config,
  };

  function syncAllSelectors() {
    const selected = DisplayCurrency.getSelected();
    document.querySelectorAll('[data-display-currency-label]').forEach((label) => {
      label.textContent = selected;
    });
    document.querySelectorAll('[data-display-currency-symbol]').forEach((symbol) => {
      symbol.textContent = SYMBOLS[selected] || '';
    });
    document.querySelectorAll('[data-display-currency-option]').forEach((option) => {
      const isSelected = option.dataset.displayCurrencyOption === selected;
      option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });
  }

  window.DisplayCurrency = DisplayCurrency;
  DisplayCurrency.syncAllSelectors = syncAllSelectors;

  document.addEventListener('display-currency:change', syncAllSelectors);

  class DisplayCurrencySelector extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.addEventListener('click', (event) => {
        const option = event.target.closest('[data-display-currency-option]');
        if (!option) return;

        event.preventDefault();
        event.stopPropagation();
        DisplayCurrency.setSelected(option.dataset.displayCurrencyOption);

        const disclosure = this.querySelector('dropdown-menu-disclosure');
        disclosure?.toggle?.(false);
      });

      syncAllSelectors();
    }
  }

  if (!window.customElements.get('display-currency-selector')) {
    window.customElements.define('display-currency-selector', DisplayCurrencySelector);
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.matches?.('[data-money-cents]')) {
          updateElement(node);
        } else {
          apply(node);
        }
      });
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    apply();
    observer.observe(document.body, { childList: true, subtree: true });
  });

  document.addEventListener('display-currency:change', (event) => {
    document.documentElement.dataset.displayCurrency = event.detail.code;
  });

  document.documentElement.dataset.displayCurrency = DisplayCurrency.getSelected();
})();
